import LgpdNotice from '@/components/feature/LgpdNotice';
import { useEffect, useState } from 'react';
import { depoimentosPalestra } from '../../mocks/depoimentos';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import AnimatedStatCard from '@/components/feature/AnimatedStatCard';

declare const gtag: (command: string, action: string, params?: Record<string, unknown>) => void;

// ─── Quote Form ─────────────────────────────────────────────────────────────

function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    orgao: '',
    eventType: '',
    participants: '',
    date: '',
    idiomas: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));

      const res = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          orgao: '',
          eventType: '',
          participants: '',
          date: '',
          idiomas: '',
          message: '',
        });
        if (typeof gtag !== 'undefined') {
          gtag('event', 'generate_lead', { event_category: 'Form', event_label: 'Brasilia Quote' });
        }
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setStatus('idle'), 8000);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm text-gray-800 bg-white transition-colors';
  const labelClass = 'block font-body text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="brasilia-quote-form"
      aria-label="Formulário de orçamento palestra silenciosa Brasília"
      className="space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bsb-name" className={labelClass}>Nome Completo *</label>
          <input
            type="text"
            id="bsb-name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Seu nome"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="bsb-email" className={labelClass}>E-mail *</label>
          <input
            type="email"
            id="bsb-email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="seu@email.gov.br"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bsb-phone" className={labelClass}>Telefone / WhatsApp *</label>
          <input
            type="tel"
            id="bsb-phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="(61) 9 9999-9999"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="bsb-orgao" className={labelClass}>Órgão / Empresa</label>
          <input
            type="text"
            id="bsb-orgao"
            name="orgao"
            value={formData.orgao}
            onChange={handleChange}
            placeholder="Ministério, Autarquia, Empresa..."
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bsb-eventType" className={labelClass}>Tipo de Evento *</label>
          <select
            id="bsb-eventType"
            name="eventType"
            required
            value={formData.eventType}
            onChange={handleChange}
            className={inputClass}
          >
            <option value="">Selecione...</option>
            <option value="Conferência Governamental">Conferência Governamental</option>
            <option value="Evento Diplomático">Evento Diplomático</option>
            <option value="Congresso / Seminário">Congresso / Seminário</option>
            <option value="Treinamento Corporativo">Treinamento Corporativo</option>
            <option value="Licitação / Lote Público">Licitação / Lote Público</option>
            <option value="Evento Multilíngue">Evento Multilíngue</option>
            <option value="Workshop / Capacitação">Workshop / Capacitação</option>
            <option value="Outro">Outro</option>
          </select>
        </div>
        <div>
          <label htmlFor="bsb-participants" className={labelClass}>Número de Participantes</label>
          <select
            id="bsb-participants"
            name="participants"
            value={formData.participants}
            onChange={handleChange}
            className={inputClass}
          >
            <option value="">Selecione...</option>
            <option value="Até 50">Até 50</option>
            <option value="50 a 150">50 a 150</option>
            <option value="150 a 300">150 a 300</option>
            <option value="300 a 500">300 a 500</option>
            <option value="Acima de 500">Acima de 500</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bsb-date" className={labelClass}>Data do Evento</label>
          <input
            type="date"
            id="bsb-date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="bsb-idiomas" className={labelClass}>Idiomas Necessários</label>
          <input
            type="text"
            id="bsb-idiomas"
            name="idiomas"
            value={formData.idiomas}
            onChange={handleChange}
            placeholder="Ex.: Português, Inglês, Espanhol"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="bsb-message" className={labelClass}>Detalhes do Evento</label>
        <textarea
          id="bsb-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          maxLength={500}
          rows={4}
          placeholder="Descreva o local, necessidades específicas, número de idiomas simultâneos..."
          className={`${inputClass} resize-none`}
        />
        <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
      </div>

      <LgpdNotice kind="orcamento" className="mb-4 col-span-full" />
            <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-primary text-white py-4 rounded-full font-body font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'ENVIANDO...' : 'SOLICITAR ORÇAMENTO GRATUITO'}
      </button>

      {status === 'success' && (
        <p className="text-center text-green-600 font-body text-sm font-semibold" role="status">
          Orçamento enviado! Nossa equipe retorna em até 24 horas.
        </p>
      )}
      {status === 'error' && (
        <p className="text-center text-red-500 font-body text-sm" role="alert">
          Erro ao enviar. Por favor, tente novamente ou ligue: (61) 99848-9988
        </p>
      )}
    </form>
  );
}

// ─── FAQ Item ────────────────────────────────────────────────────────────────

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-gray-100 rounded-2xl overflow-hidden"
      itemScope
      itemType="https://schema.org/Question"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-7 py-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
        aria-expanded={open}
        id={`faq-bsb-${index}`}
      >
        <h4 className="font-body font-semibold text-secondary text-base leading-snug pr-4" itemProp="name">
          {question}
        </h4>
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary/10">
          <i className={`ri-${open ? 'subtract' : 'add'}-line text-primary text-base`}></i>
        </div>
      </button>
      {open && (
        <div
          className="px-7 pb-6 bg-white"
          itemScope
          itemType="https://schema.org/Answer"
          itemProp="acceptedAnswer"
        >
          <p className="font-body text-gray-600 text-sm leading-relaxed" itemProp="text">{answer}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const TESTIMONIALS = depoimentosPalestra.slice(0, 3).map((d) => ({
  name: d.nome,
  text: d.texto,
  context: d.contexto,
  rating: 5,
}));

const FAQS = [
  {
    question: 'A palestra silenciosa atende exigências de licitação pública no DF?',
    answer: 'Sim. Emitimos nota fiscal eletrônica, contrato formal e toda documentação exigida por processos licitatórios (pregão, dispensa, inexigibilidade). Atendemos órgãos federais, autarquias, fundações públicas e ministérios em Brasília com toda conformidade fiscal.',
  },
  {
    question: 'Quantos idiomas simultâneos o sistema suporta em Brasília?',
    answer: 'Nosso sistema suporta até 10 canais simultâneos — ideal para eventos multilíngues em Brasília como conferências do MERCOSUL, cúpulas da OEA, reuniões com delegações internacionais. Os participantes escolhem o idioma no próprio fone, sem necessidade de cabines físicas.',
  },
  {
    question: 'Como funciona a logística de entrega e montagem em Brasília?',
    answer: 'Realizamos entrega e instalação em todo o Distrito Federal: Plano Piloto, Asa Norte, Asa Sul, Lago Sul, Lago Norte, Taguatinga, Ceilândia, Gama e cidades satélites. Nossa equipe chega com 2h de antecedência para setup, testes de sinal e treinamento dos operadores do evento.',
  },
  {
    question: 'Os equipamentos funcionam no Complexo Brasil 21 e na Arena Mané Garrincha?',
    answer: 'Sim, com excelência. Já realizamos mais de 30 eventos no Complexo Brasil 21 e na Arena Mané Garrincha, incluindo congressos de grande porte. O sinal sem fio cobre toda a área das salas e da arena sem interferências, mesmo em estruturas com paredes especiais e blindagem metálica.',
  },
  {
    question: 'Vocês atendem eventos do tipo diplomático com requisitos de segurança eletrônica?',
    answer: 'Sim. Utilizamos frequências certificadas pela Anatel (banda RF 863–865 MHz), compatíveis com os protocolos de segurança eletrônica de instalações sensíveis. Para eventos de alto nível diplomático, realizamos varredura prévia e operamos com equipamentos de baixa emissão EMI.',
  },
  {
    question: 'Qual a antecedência ideal para contratar palestra silenciosa em Brasília?',
    answer: 'Recomendamos contato com pelo menos 15 dias de antecedência para eventos de médio e grande porte. Para eventos menores (até 100 participantes), conseguimos atender com 7 dias. Em casos de licitação, recomendamos 30 dias para preparar toda a documentação necessária.',
  },
];

const VENUES = [
  { name: 'Complexo Brasil 21', icon: 'ri-building-4-line', capacity: '1.500' },
  { name: 'Espaço Dois Ipês', icon: 'ri-leaf-line', capacity: '600' },
  { name: 'Arena Mané Garrincha', icon: 'ri-building-2-line', capacity: '70.000' },
  { name: 'Royal Tulip Brasília', icon: 'ri-hotel-line', capacity: '800' },
  { name: 'Itamaraty (MRE)', icon: 'ri-government-line', capacity: '400' },
  { name: 'UnB — Auditório Dois Candangos', icon: 'ri-graduation-cap-line', capacity: '350' },
  { name: 'ENAP — Auditório', icon: 'ri-book-open-line', capacity: '250' },
  { name: 'Senado Federal', icon: 'ri-bank-line', capacity: '300' },
  { name: 'IPEA — Auditório', icon: 'ri-bar-chart-line', capacity: '200' },
  { name: 'Windsor Brasília Hotel', icon: 'ri-hotel-line', capacity: '600' },
];

export default function BrasiliaLandingPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitsRef, isVisible: benefitsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: venuesRef, isVisible: venuesVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: testimonialsRef, isVisible: testimonialsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    document.title = 'Palestra Silenciosa Brasília | Eventos Governamentais e Corporativos DF | STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Palestra silenciosa Brasília: tecnologia premium para eventos governamentais, conferências diplomáticas e corporativos no DF. Tradução simultânea em 3 idiomas, nota fiscal para licitação. Orçamento em 24h!'
      );
    }

    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute('content', '-15.7942;-47.8822');
    const geoReg = document.querySelector('meta[name="geo.region"]');
    if (geoReg) geoReg.setAttribute('content', 'BR-DF');
    const geoPlace = document.querySelector('meta[name="geo.placename"]');
    if (geoPlace) geoPlace.setAttribute('content', 'Brasília');

    const schemas = [
      {
        id: 'bsb-localbusiness',
        data: {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'STIB Palestra Silenciosa Brasília',
          description: 'Aluguel de fones sem fio e sistemas de palestra silenciosa para eventos governamentais, corporativos e diplomáticos em Brasília — DF.',
          url: `${siteUrl}/brasilia`,
          telephone: '+55-61-99848-9988',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Brasília',
            addressRegion: 'DF',
            addressCountry: 'BR',
          },
          geo: { '@type': 'GeoCoordinates', latitude: -15.7942, longitude: -47.8822 },
          areaServed: { '@type': 'City', name: 'Brasília' },
        },
      },
      {
        id: 'bsb-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Palestra Silenciosa Brasília', item: `${siteUrl}/brasilia` },
          ],
        },
      },
      {
        id: 'bsb-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
        aria-label="Palestra Silenciosa Brasília — Hero"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/cidade-brasilia.webp"
            alt="Palestra Silenciosa Brasília — Eventos governamentais e corporativos no DF"
            title="Palestra Silenciosa Brasília — STIB"
            className={`w-full h-full object-cover object-top transition-all duration-1200 ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black/70"></div>
        </div>

        <div className="absolute top-24 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb
              variant="light"
              items={[
                { label: 'Home', to: '/' },
                { label: 'Cidades', to: '/cidades' },
                { label: 'Palestra Silenciosa Brasília' },
              ]}
            />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <div
            className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-primary text-sm"></i>
            </div>
            Brasília — Distrito Federal
          </div>

          <header>
            <h1
              className={`font-heading font-extrabold text-6xl md:text-8xl text-white mb-6 tracking-tight leading-none lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              palestra silenciosa<br />
              <span className="text-primary">brasília</span>
            </h1>
          </header>

          <p
            className={`font-body font-light text-xl md:text-2xl text-white/85 mb-10 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            Tecnologia premium de áudio sem fio para eventos governamentais, conferências diplomáticas
            e corporativos no DF. Tradução simultânea em 3 idiomas sem cabine física.
          </p>

          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-arrow-down-line text-white text-lg"></i>
              </div>
            </a>
            <a
              href="https://wa.me/5561999990000?text=Olá! Preciso de orçamento para palestra silenciosa em Brasília."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              </div>
              WhatsApp DF
            </a>
          </div>

          {/* Trust badges */}
          <div
            className={`flex flex-wrap items-center justify-center gap-6 mt-14 transition-all duration-700 delay-400 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {[
              { icon: 'ri-shield-check-line', label: 'Nota fiscal para licitação' },
              { icon: 'ri-translate-2', label: 'Até 10 idiomas simultâneos' },
              { icon: 'ri-timer-line', label: 'Resposta em 24 horas' },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-white/80 text-sm font-body">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${b.icon} text-primary text-base`}></i>
                </div>
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────── */}
      <section ref={statsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { numericValue: 80, suffix: '+', label: 'Órgãos Atendidos', icon: 'ri-government-line' },
              { numericValue: 3, suffix: '', label: 'Idiomas Simultâneos', icon: 'ri-translate-2' },
              { numericValue: 99, suffix: '%', label: 'Satisfação dos Clientes', icon: 'ri-star-line' },
            ].map((s, i) => (
              <AnimatedStatCard
                key={i}
                numericValue={s.numericValue}
                suffix={s.suffix}
                label={s.label}
                icon={s.icon}
                delay={i * 100}
                visible={statsVisible}
                numberColor="text-secondary"
                className="p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5"
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM + INFO ──────────────────────────────────────── */}
      <section
        id="orcamento"
        ref={formRef}
        className="py-24 bg-gradient-to-br from-gray-50 to-white"
        aria-label="Formulário de orçamento palestra silenciosa Brasília"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Info lateral */}
            <div
              className={`transition-all duration-700 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            >
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Orçamento Gratuito · Retorno em 24 Horas
              </span>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase leading-tight">
                quanto custa palestra silenciosa em brasília?
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                Preencha o formulário ao lado e receba um orçamento detalhado, com nota fiscal e contrato,
                adequado à sua modalidade de contratação pública ou privada.
              </p>

              <div className="space-y-6 mb-10">
                {[
                  {
                    icon: 'ri-file-list-3-line',
                    title: 'Documentação Completa',
                    desc: 'NF-e, contrato, ART quando necessário. Atendemos dispensa, pregão e inexigibilidade.',
                  },
                  {
                    icon: 'ri-translate-2',
                    title: 'Tradução Simultânea Sem Cabine',
                    desc: 'Economia de 50–70% vs cabines físicas. Perfeito para cúpulas, conferências do MERCOSUL e eventos multilíngues.',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Frequências Certificadas Anatel',
                    desc: 'Compatível com ambientes de segurança eletrônica. Ideal para Itamaraty, Senado e Ministérios.',
                  },
                  {
                    icon: 'ri-tools-line',
                    title: 'Técnico Presente no Evento',
                    desc: 'Setup, testes e suporte em tempo real durante todo o evento no DF.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <i className={`${item.icon} text-primary text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-secondary text-base mb-1">{item.title}</h3>
                      <p className="font-body text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-secondary/5 border border-secondary/10 rounded-2xl p-6">
                <p className="font-body text-sm text-gray-600 leading-relaxed">
                  <strong className="text-secondary">Atendimento em Brasília — 2026:</strong><br />
                  Equipamentos certificados pela Anatel (RF 863–865 MHz)<br />
                  Nota fiscal eletrônica para contratação pública<br />
                  Setup técnico incluso em todos os pacotes
                </p>
              </div>
            </div>

            {/* Formulário */}
            <div
              className={`bg-white rounded-3xl p-10 border border-gray-100 transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary">
                  <i className="ri-send-plane-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-secondary">Solicite seu Orçamento</h3>
                  <p className="font-body text-xs text-gray-400">Gratuito · Sem compromisso · Resposta em 24h</p>
                </div>
              </div>
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFÍCIOS / DIFERENCIAIS BSB ─────────────────── */}
      <section ref={benefitsRef} className="py-24 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Por que escolher a STIB em Brasília
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl lowercase transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              feito para o mercado do DF
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-government-line',
                title: 'Especialistas em Eventos Governamentais',
                desc: 'Entendemos as exigências de contratos públicos, prazos de licitação e protocolos de segurança eletrônica de instalações sensíveis em Brasília.',
              },
              {
                icon: 'ri-global-line',
                title: 'Conferências Multilíngues e Diplomáticas',
                desc: 'Até 10 canais de idiomas simultâneos. Já operamos em eventos com delegações de 20+ países, incluindo cúpulas do MERCOSUL e eventos da OEA no DF.',
              },
              {
                icon: 'ri-building-4-line',
                title: 'Expertise nos Principais Espaços de BSB',
                desc: 'Operação recorrente no CCUG, Royal Tulip, UnB, ENAP, Itamaraty e hoteis de convenção de Brasília — zero surpresas logísticas.',
              },
              {
                icon: 'ri-file-list-3-line',
                title: 'Nota Fiscal para Contratação Pública',
                desc: 'NF-e, contratos e toda a documentação exigida em processos de dispensa, pregão eletrônico ou inexigibilidade no DF.',
              },
              {
                icon: 'ri-wifi-line',
                title: 'Frequências Certificadas pela Anatel',
                desc: 'Banda RF 863–865 MHz, compatível com ambientes de alta segurança eletrônica. Sem interferências com os sistemas de comunicação governamentais.',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Suporte Técnico no Local',
                desc: 'Técnico dedicado do setup até o encerramento. Resposta imediata a qualquer problema em tempo real durante o seu evento em Brasília.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/20 mb-6">
                  <i className={`${item.icon} text-primary text-2xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-white text-lg mb-3">{item.title}</h3>
                <p className="font-body text-white/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ESPAÇOS / VENUES ─────────────────────────────── */}
      <section ref={venuesRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div
              className={`transition-all duration-700 ${venuesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            >
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Onde já atuamos
              </span>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase leading-tight">
                principais espaços em brasília
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                Já realizamos eventos de <strong>palestra silenciosa em Brasília</strong> nos principais
                centros de convenções, hoteis e instalações governamentais do DF. Conhecemos cada detalhe
                logístico desses espaços.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {VENUES.map((v, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-4 p-4 rounded-xl bg-gray-50 transition-all duration-500 ${venuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: `${i * 60}ms` }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <i className={`${v.icon} text-primary text-base`}></i>
                    </div>
                    <div>
                      <p className="font-body font-semibold text-secondary text-sm">{v.name}</p>
                      <p className="font-body text-xs text-gray-400">até {v.capacity} pessoas</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className={`relative h-[520px] rounded-3xl overflow-hidden transition-all duration-700 delay-200 ${venuesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
            >
              <img
                src="/img/stib/local-auditorio.webp"
                alt="Centro de Convenções em Brasília — Palestra Silenciosa DF"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <p className="font-body text-white/80 text-sm mb-2">Atendimento completo em</p>
                <h3 className="font-heading font-bold text-white text-2xl">Brasília e todo o DF</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEPOIMENTOS ──────────────────────────────────── */}
      <section
        ref={testimonialsRef}
        className="py-24 bg-gradient-to-br from-gray-50 to-white"
        aria-label="Depoimentos de clientes Brasília"
        itemScope
        itemType="https://schema.org/ItemList"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Quem já contratou no DF
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              o que dizem nossos clientes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-8 border border-gray-100 transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(t.rating)].map((_, j) => (
                    <i key={j} className="ri-star-fill text-yellow-400 text-base"></i>
                  ))}
                </div>
                <p className="font-body text-gray-600 text-sm leading-relaxed mb-6 italic">
                  "{t.text}"
                </p>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary/10 flex-shrink-0">
                    <span className="font-heading font-bold text-secondary text-lg">
                      {t.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-secondary text-sm">{t.name}</p>
                    {t.context && (
                      <p className="font-body text-xs text-gray-500">{t.context}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────── */}
      <section
        ref={faqRef}
        className="py-24 bg-white"
        aria-label="Perguntas frequentes palestra silenciosa Brasília"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Dúvidas Frequentes
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              faq — brasília &amp; df
            </h2>
          </div>

          <div
            className={`space-y-4 transition-all duration-700 delay-200 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {FAQS.map((f, i) => (
              <FAQItem key={i} question={f.question} answer={f.answer} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────── */}
      <section className="relative py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/stib/cidade-brasilia.webp"
            alt="Solicite orçamento palestra silenciosa Brasília"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/85 via-secondary/70 to-secondary/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="max-w-2xl">
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-white lowercase leading-tight mb-6">
                pronto para transformar<br />
                seu evento em brasília?
              </h2>
              <p className="font-body text-lg text-white/80 leading-relaxed">
                <strong className="text-white">Apenas 3 vagas disponíveis</strong> para eventos em Brasília em junho. 
                Confirme sua data e receba orçamento completo em até 24 horas.
              </p>
            </div>

            <div className="flex flex-col gap-4 flex-shrink-0">
              <a
                href="/orcamento"
                className="inline-flex items-center justify-center gap-3 bg-primary text-white px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                VER FORMULÁRIO DE ORÇAMENTO
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-arrow-up-line text-white text-lg"></i>
                </div>
              </a>
              <a
                href="https://wa.me/5561999990000?text=Olá! Preciso de orçamento para palestra silenciosa em Brasília."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white text-secondary px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-green-500 text-xl"></i>
                </div>
                FALAR NO WHATSAPP
              </a>
              <Link
                to="/blog/palestra-silenciosa-brasilia-guia-completo"
                className="text-center font-body text-sm text-white/70 hover:text-white underline underline-offset-4 transition-colors cursor-pointer"
              >
                Ler nosso guia completo sobre Palestra Silenciosa Brasília &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}